import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { FruitifyContainer } from "../../../shared-components/Containers";
import GeneralLoading from "../../../shared-components/Loading";
import { useAuthenticationStore } from "../../../stores/auth";
import { useApiClient } from "../../../api";

const ModeratorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticationStore();
  const ApiClient = useApiClient();

  const [selectedEducationLevel, setSelectedEducationLevel] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [availableCount, setAvailableCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [fetchingAnswer, setFetchingAnswer] = useState(false);

  const moderator = user?.info?.activeContext?.moderator;
  const educationLevels = moderator?.educationLevels || [];
  const subjects = moderator?.subjects || [];

  useEffect(() => {
    if (educationLevels.length === 1) {
      setSelectedEducationLevel(educationLevels[0].id);
    }
    if (subjects.length === 1) {
      setSelectedSubject(subjects[0].id);
    }
  }, [educationLevels, subjects]);

  useEffect(() => {
    const fetchCount = async () => {
      if (selectedEducationLevel && selectedSubject) {
        setCountLoading(true);
        try {
          const count = await ApiClient.moderator.getModeratorAvailableCount({
            educationLevel: selectedEducationLevel,
            subject: selectedSubject,
          });
          setAvailableCount(count);
        } catch (error) {
          enqueueSnackbar(`Error fetching count: ${error.message}`, {
            variant: "error",
          });
        } finally {
          setCountLoading(false);
        }
      }
    };
    fetchCount();
  }, [selectedEducationLevel, selectedSubject]);

  const handleFetchNext = async () => {
    if (selectedEducationLevel && selectedSubject) {
      setFetchingAnswer(true);
      try {
        const answer = await ApiClient.moderator.getNextModeratorAnswer({
          educationLevel: selectedEducationLevel,
          subject: selectedSubject,
        });
        if (answer) {
          navigate(`/moderator/answer/${answer.id}`);
        } else {
          setAvailableCount(0);
          enqueueSnackbar("No more answers available for moderation", {
            variant: "info",
          });
        }
      } catch (error) {
        enqueueSnackbar(`Error fetching answer: ${error.message}`, {
          variant: "error",
        });
      } finally {
        setFetchingAnswer(false);
      }
    }
  };

  const canFetchNext =
    selectedEducationLevel && selectedSubject && availableCount > 0;

  return (
    <FruitifyContainer>
      <Typography variant="h4" gutterBottom>
        Moderate Answers
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Education Level and Subject
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="education-level-label">
                Education Level
              </InputLabel>
              <Select
                labelId="education-level-label"
                id="education-level-select"
                value={selectedEducationLevel}
                onChange={(e) => setSelectedEducationLevel(e.target.value)}
                disabled={educationLevels.length === 1}
                label="Education Level"
              >
                {educationLevels.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="subject-label">Subject</InputLabel>
              <Select
                labelId="subject-label"
                id="subject-select"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                disabled={subjects.length === 1}
                label="Subject"
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {countLoading ? (
            <GeneralLoading />
          ) : availableCount !== null ? (
            <Box>
              <Alert
                severity={availableCount > 0 ? "info" : "warning"}
                sx={{ mb: 2 }}
              >
                <Typography variant="body1">
                  <strong>{availableCount}</strong> answer
                  {availableCount !== 1 ? "s" : ""} available for moderation
                </Typography>
              </Alert>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleFetchNext}
                disabled={!canFetchNext || fetchingAnswer}
                fullWidth
              >
                {fetchingAnswer ? "Loading..." : "Fetch Next Answer"}
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Instructions
          </Typography>
          <Typography variant="body2" paragraph>
            1. Select an education level and subject from the dropdowns above
          </Typography>
          <Typography variant="body2" paragraph>
            2. Click "Fetch Next Answer" to start moderating
          </Typography>
          <Typography variant="body2" paragraph>
            3. Review the student's answer and adjust the marks as needed
          </Typography>
          <Typography variant="body2" paragraph>
            4. Click "Submit" to approve your moderation
          </Typography>
        </CardContent>
      </Card>
    </FruitifyContainer>
  );
};

export default ModeratorDashboard;
